// frontend/src/pages/Expenses/Expenses.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  UserPlus, Save, X, Loader2, Building, AlertCircle,
  RefreshCw, User, Hash, Info, Tag, Calendar, DollarSign,
  FileText, CreditCard, Banknote, Smartphone, Wallet, Layers
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./Expenses.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

// ✅ Auto-generation helpers (used when the user leaves fields empty)
const buildAutoCode = (prefix) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${y}${m}${d}-${rand}`;
};

const generateAutoInvoiceNumber = () => buildAutoCode("INV");
const generateAutoReferenceNumber = () => buildAutoCode("REF");

// ✅ Shared inline styles
const sharedFieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const sharedInputStyle = {
  width: "100%",
  minHeight: "44px",
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid #d9d9d9",
  fontSize: "14px",
  boxSizing: "border-box",
  background: "#ffffff",
  color: "#170f48",
};

const sharedSelectWrapperStyle = {
  position: "relative",
  display: "flex",
  alignItems: "center",
};

const sharedSelectStyle = {
  width: "100%",
  minHeight: "44px",
  padding: "10px 36px 10px 12px",
  borderRadius: "6px",
  border: "1px solid #d9d9d9",
  fontSize: "14px",
  boxSizing: "border-box",
  background: "#ffffff",
  appearance: "none",
  cursor: "pointer",
  color: "#170f48",
};

const sharedSelectIconStyle = {
  position: "absolute",
  right: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#8c8c8c",
  pointerEvents: "none",
};

const sharedLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontWeight: 500,
  color: "#170f48",
  margin: 0,
};

const sharedTextareaStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid #d9d9d9",
  fontSize: "14px",
  boxSizing: "border-box",
  minHeight: "90px",
  fontFamily: "inherit",
  resize: "vertical",
  color: "#170f48",
};

const sharedHelperTextStyle = {
  fontSize: "12px",
  color: "#8c8c8c",
  marginTop: "2px",
};

const sharedAutoTextStyle = {
  fontSize: "12px",
  color: "#1890ff",
  marginTop: "2px",
  fontStyle: "italic",
};

const sharedErrorTextStyle = {
  fontSize: "12px",
  color: "#dc3545",
  marginTop: "2px",
};

const Expenses = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return formatCurrency(0, companyCurrency);
    return formatCurrency(amount, companyCurrency);
  };

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [paymentTypes, setPaymentTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const [expenseTypeAccounts, setExpenseTypeAccounts] = useState([]);
  const [loadingExpenseTypes, setLoadingExpenseTypes] = useState(false);

  // ✅ Stable auto-generated preview values for this form session.
  // Regenerated only when the page is (re)mounted.
  const [autoInvoicePreview] = useState(() => generateAutoInvoiceNumber());
  const [autoReferencePreview] = useState(() => generateAutoReferenceNumber());

  const [formData, setFormData] = useState({
    expense_name: "",
    expense_date: "",
    amount: "",
    invoice_number: "",
    description: "",
    account_id: "",
    expense_account_id: "",
    payment_method: "",
    reference_number: "",
    company_id: ""
  });

  const paymentMethodOptions = [
    { value: "Cash", label: "Cash" },
    { value: "Bank", label: "Bank" },
    { value: "Mobile Money", label: "Mobile Money" },
    { value: "Credit", label: "Credit" }
  ];

  const isSystemAdminUser = () =>
    userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  const isCompanyAdminUser = () =>
    userType === 'company' && (currentUser?.role === 'admin' || currentUser?.role === 'cashier' || currentUser?.role === 'user');

  const userCanCreateExpense = () => isCompanyAdminUser() || isSystemAdminUser();

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`, { headers: getAuthHeaders() });
      if (response.data.success) setCompanies(response.data.data || []);
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchPaymentTypes = async (companyId) => {
    if (!companyId) return;
    try {
      const response = await axios.get(`${url}/api/paymenttype/dropdown`, {
        headers: getAuthHeaders(),
        params: { company_id: companyId, include_inactive: false }
      });
      if (response.data.success) setPaymentTypes(response.data.data || []);
    } catch (error) {} finally {}
  };

  const fetchAllAccounts = async (companyId) => {
    if (!companyId) return;
    try {
      setLoadingAccounts(true);
      const response = await axios.get(`${url}/api/account/dropdown`, {
        headers: getAuthHeaders(),
        params: { company_id: companyId },
      });
      if (response.data.success) {
        setAllAccounts(response.data.data || []);
        setAccounts(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchExpenseTypeAccounts = async (companyId) => {
    if (!companyId) return;
    try {
      setLoadingExpenseTypes(true);
      const response = await axios.get(`${url}/api/account/dropdown`, {
        headers: getAuthHeaders(),
        params: {
          company_id: companyId,
          type: "Expense",
          include_inactive: "false",
        },
      });
      if (response.data.success) {
        const list = (response.data.data || []).filter((acc) => {
          const code = String(acc.account_code || "");
          return code.startsWith("5") || code.startsWith("6") || code.startsWith("7");
        });
        list.sort((a, b) =>
          String(a.account_code).localeCompare(String(b.account_code), undefined, { numeric: true })
        );
        setExpenseTypeAccounts(list);
      }
    } catch (error) {
      setExpenseTypeAccounts([]);
    } finally {
      setLoadingExpenseTypes(false);
    }
  };

  const filterAccountsByPaymentMethod = (paymentMethod) => {
    if (!paymentMethod) { setAccounts([]); return; }
    const filtered = allAccounts.filter((account) => {
      if (account.is_control_account === true || !account.parent_account) return false;
      const name = (account.account_name || "").toLowerCase();
      if (paymentMethod === "Cash") return name.includes("cash") || name.includes("petty");
      if (paymentMethod === "Bank") return name.includes("bank");
      if (paymentMethod === "Mobile Money") return name.includes("mobile") || name.includes("evc") || name.includes("e-dahab") || name.includes("wallet");
      if (paymentMethod === "Credit") return name.includes("credit") || name.includes("payable") || name.includes("receivable");
      return false;
    });
    setAccounts(filtered);
  };

  useEffect(() => {
    const checkPermissionsAndSetup = async () => {
      try {
        if (!userCanCreateExpense()) {
          toast.error("You don't have permission to create expenses");
          navigate("/");
          return;
        }

        if (isSystemAdminUser()) {
          await fetchCompanies();
        } else if (userType === 'company' && company) {
          const companyId = company._id || company;
          setFormData(prev => ({
            ...prev,
            company_id: companyId,
            payment_method: "Cash"
          }));
          await fetchPaymentTypes(companyId);
          await fetchAllAccounts(companyId);
          await fetchExpenseTypeAccounts(companyId);
        }

        setFormData(prev => ({
          ...prev,
          expense_date: new Date().toISOString().split('T')[0]
        }));

        setLoading(false);
      } catch (error) {
        toast.error("Failed to load permissions");
        navigate("/");
      }
    };
    checkPermissionsAndSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSystemAdminUser() && formData.company_id) {
      fetchPaymentTypes(formData.company_id);
      fetchAllAccounts(formData.company_id);
      fetchExpenseTypeAccounts(formData.company_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.company_id]);

  useEffect(() => {
    if (formData.payment_method) {
      filterAccountsByPaymentMethod(formData.payment_method);
    } else {
      setAccounts(allAccounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.payment_method, allAccounts]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handlePaymentMethodChange = (e) => {
    const paymentMethod = e.target.value;
    setFormData(prev => ({ ...prev, payment_method: paymentMethod, account_id: "" }));
    if (errors.payment_method) setErrors(prev => ({ ...prev, payment_method: "" }));
    filterAccountsByPaymentMethod(paymentMethod);
  };

  const handleCompanyChange = async (e) => {
    const value = e.target.value;
    setSelectedCompany(value);
    setFormData(prev => ({
      ...prev,
      company_id: value,
      account_id: "",
      expense_account_id: "",
      payment_method: "Cash"
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.expense_name.trim()) newErrors.expense_name = "Expense name is required";
    else if (formData.expense_name.trim().length < 2) newErrors.expense_name = "Expense name must be at least 2 characters";
    if (!formData.expense_date) newErrors.expense_date = "Expense date is required";
    if (!formData.amount) newErrors.amount = "Amount is required";
    else if (parseFloat(formData.amount) <= 0) newErrors.amount = "Amount must be greater than 0";
    if (!formData.expense_account_id) newErrors.expense_account_id = "Expense type is required";
    if (!formData.account_id) newErrors.account_id = "Payment account is required";
    if (!formData.payment_method) newErrors.payment_method = "Payment method is required";
    if (!formData.company_id) newErrors.company_id = "Company selection is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    try {
      setSubmitting(true);

      // ✅ Auto-fill empty fields using the stable previews
      const finalInvoiceNumber =
        formData.invoice_number?.trim() || autoInvoicePreview;

      const finalReferenceNumber =
        formData.reference_number?.trim() || autoReferencePreview;

      const finalDescription =
        formData.description?.trim() ||
        `${finalInvoiceNumber} - ${finalReferenceNumber}`;

      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        expense_account_id: formData.expense_account_id,
        invoice_number: finalInvoiceNumber,
        reference_number: finalReferenceNumber,
        description: finalDescription,
      };

      const response = await axios.post(`${url}/api/expense`, submitData, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        toast.success(response.data.message || "Expense created successfully!");

        // Generate fresh previews for the next expense
        setFormData({
          expense_name: "",
          expense_date: new Date().toISOString().split('T')[0],
          amount: "",
          invoice_number: "",
          description: "",
          account_id: "",
          expense_account_id: "",
          payment_method: "Cash",
          reference_number: "",
          company_id: formData.company_id
        });

        setTimeout(() => {
          navigate("/ViewExpenses");
        }, 1000);
      } else {
        toast.error(response.data.message || "Failed to create expense");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to create expense";
      toast.error(errorMsg);
      if (error.response?.data?.errors) {
        setErrors(prev => ({
          ...prev,
          ...Object.fromEntries(error.response.data.errors.map(err => [err.field, err.message]))
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate("/ViewExpenses");

  const selectedAccount = accounts.find(acc => acc._id === formData.account_id);
  const selectedExpenseType = expenseTypeAccounts.find(acc => acc._id === formData.expense_account_id);

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="expense-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="expense-form-container">
        <button className="close-btn" onClick={handleCancel} title="Close">
          <X size={20} />
        </button>

        <div className="form-header">
          <UserPlus size={24} />
          <h2 className="form-title">Add New Expense</h2>
          <p className="form-subtitle">
            {isSystemAdminUser()
              ? "Record an expense for any company"
              : `Record an expense for ${company?.company_name || 'your company'}`}
          </p>
        </div>

        <div className="role-indicator">
          <Building size={20} />
          <div>
            <strong>{isSystemAdminUser() ? "System Administrator" : "Company Admin"}</strong>
            <div className="role-subtitle">
              {isSystemAdminUser()
                ? "You can record expenses for any company"
                : `You can only record expenses for ${company?.company_name || 'your company'}`}
            </div>
          </div>
        </div>

        <form className="expense-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {isSystemAdminUser() && (
              <div style={sharedFieldStyle} className={errors.company_id ? 'error' : ''}>
                <div style={sharedSelectWrapperStyle}>
                  <select
                    name="company_id"
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    required
                    disabled={loadingCompanies}
                    style={sharedSelectStyle}
                  >
                    <option value="">Select Company</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.company_name} ({comp.company_code})
                      </option>
                    ))}
                  </select>
                  <Building size={16} style={sharedSelectIconStyle} />
                </div>
                <label style={sharedLabelStyle}><Building size={14} />Company</label>
                {errors.company_id && <small style={sharedErrorTextStyle}>{errors.company_id}</small>}
              </div>
            )}

            <div style={sharedFieldStyle} className={errors.expense_name ? 'error' : ''}>
              <input
                type="text"
                name="expense_name"
                value={formData.expense_name}
                onChange={handleChange}
                placeholder="Enter expense name"
                required
                style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><Tag size={14} />Expense Name</label>
              {errors.expense_name && <small style={sharedErrorTextStyle}>{errors.expense_name}</small>}
            </div>

            <div style={sharedFieldStyle} className={errors.expense_date ? 'error' : ''}>
              <input
                type="date"
                name="expense_date"
                value={formData.expense_date}
                onChange={handleChange}
                required
                style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><Calendar size={14} />Expense Date</label>
              {errors.expense_date && <small style={sharedErrorTextStyle}>{errors.expense_date}</small>}
            </div>

            <div style={sharedFieldStyle} className={errors.amount ? 'error' : ''}>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                step="0.01"
                min="0"
                required
                style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><DollarSign size={14} />Amount</label>
              {errors.amount && <small style={sharedErrorTextStyle}>{errors.amount}</small>}
              {!errors.amount && <small style={sharedHelperTextStyle}>Enter the expense amount</small>}
            </div>

            {/* ✅ Invoice Number — auto-fill preview when empty */}
            <div style={sharedFieldStyle}>
              <input
                type="text"
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
                placeholder={autoInvoicePreview}
                style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><FileText size={14} />Invoice Number</label>
              {formData.invoice_number?.trim() ? (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  Will save your manual entry: "{formData.invoice_number}"
                </small>
              ) : (
                <small style={sharedAutoTextStyle}>
                  Leave empty → auto: {autoInvoicePreview}
                </small>
              )}
            </div>

            <div style={sharedFieldStyle} className={errors.payment_method ? 'error' : ''}>
              <div style={sharedSelectWrapperStyle}>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handlePaymentMethodChange}
                  required
                  style={sharedSelectStyle}
                >
                  <option value="">Select Payment Method</option>
                  {paymentMethodOptions.map((method) => (
                    <option key={method.value} value={method.value}>{method.label}</option>
                  ))}
                </select>
                <CreditCard size={16} style={sharedSelectIconStyle} />
              </div>
              <label style={sharedLabelStyle}><CreditCard size={14} />Payment Method</label>
              {errors.payment_method && <small style={sharedErrorTextStyle}>{errors.payment_method}</small>}
            </div>

            <div style={sharedFieldStyle} className={errors.expense_account_id ? 'error' : ''}>
              <div style={sharedSelectWrapperStyle}>
                <select
                  name="expense_account_id"
                  value={formData.expense_account_id}
                  onChange={handleChange}
                  required
                  disabled={loadingExpenseTypes || expenseTypeAccounts.length === 0}
                  style={{
                    ...sharedSelectStyle,
                    background: loadingExpenseTypes || expenseTypeAccounts.length === 0 ? "#f5f5f5" : "#ffffff",
                  }}
                >
                  <option value="">
                    {loadingExpenseTypes
                      ? "Loading expense types..."
                      : expenseTypeAccounts.length === 0
                      ? "No expense accounts configured"
                      : "Select Expense Type"}
                  </option>
                  {expenseTypeAccounts.map((acc) => (
                    <option key={acc._id} value={acc._id}>
                      {acc.account_code} - {acc.account_name}
                    </option>
                  ))}
                </select>
                <Tag size={16} style={sharedSelectIconStyle} />
              </div>
              <label style={sharedLabelStyle}><Layers size={14} />Expense Type</label>
              {errors.expense_account_id && <small style={sharedErrorTextStyle}>{errors.expense_account_id}</small>}
              {!errors.expense_account_id && selectedExpenseType && (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  Journal entry will debit: {selectedExpenseType.account_code} - {selectedExpenseType.account_name}
                </small>
              )}
              {!errors.expense_account_id && !selectedExpenseType && (
                <small style={sharedHelperTextStyle}>
                  Which expense category this belongs to (Rent, Utilities, Salaries...)
                </small>
              )}
            </div>

            <div style={sharedFieldStyle} className={errors.account_id ? 'error' : ''}>
              <div style={sharedSelectWrapperStyle}>
                <select
                  name="account_id"
                  value={formData.account_id}
                  onChange={handleChange}
                  required
                  disabled={loadingAccounts || !formData.payment_method}
                  style={{
                    ...sharedSelectStyle,
                    background: loadingAccounts || !formData.payment_method ? "#f5f5f5" : "#ffffff",
                  }}
                >
                  <option value="">
                    {!formData.payment_method
                      ? "Select payment method first"
                      : loadingAccounts
                      ? "Loading accounts..."
                      : accounts.length === 0
                      ? "No accounts available"
                      : "Select Payment Account"}
                  </option>
                  {accounts.map((acc) => (
                    <option key={acc._id} value={acc._id}>
                      {acc.account_name} - Balance: {formatAmount(acc.balance || 0)}
                    </option>
                  ))}
                </select>
                <Info size={16} style={sharedSelectIconStyle} />
              </div>
              <label style={sharedLabelStyle}><Wallet size={14} />Payment Account</label>
              {errors.account_id && <small style={sharedErrorTextStyle}>{errors.account_id}</small>}
              {selectedAccount && (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  This account will be credited in the journal
                </small>
              )}
            </div>

            {/* ✅ Reference Number — auto-fill preview when empty */}
            <div style={sharedFieldStyle}>
              <input
                type="text"
                name="reference_number"
                value={formData.reference_number}
                onChange={handleChange}
                placeholder={autoReferencePreview}
                style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><Hash size={14} />Reference Number</label>
              {formData.reference_number?.trim() ? (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  Will save your manual entry: "{formData.reference_number}"
                </small>
              ) : (
                <small style={sharedAutoTextStyle}>
                  Leave empty → auto: {autoReferencePreview}
                </small>
              )}
            </div>

            {/* ✅ Description — auto-fill preview when empty */}
            <div style={{ ...sharedFieldStyle, gridColumn: "span 2" }}>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={`${formData.invoice_number?.trim() || autoInvoicePreview} - ${formData.reference_number?.trim() || autoReferencePreview}`}
                rows="3"
                style={sharedTextareaStyle}
              />
              <label style={sharedLabelStyle}><FileText size={14} />Description</label>
              {formData.description?.trim() ? (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  Will save your manual entry
                </small>
              ) : (
                <small style={sharedAutoTextStyle}>
                  Leave empty → auto: {formData.invoice_number?.trim() || autoInvoicePreview} - {formData.reference_number?.trim() || autoReferencePreview}
                </small>
              )}
            </div>

            {selectedAccount && formData.amount && parseFloat(formData.amount) > 0 && (
              <div
                className="balance-info"
                style={{
                  gridColumn: "span 2",
                  padding: "12px 16px",
                  background: selectedAccount.balance >= parseFloat(formData.amount) ? "#e6f7ff" : "#fff2f0",
                  borderRadius: "6px",
                  borderLeft: `4px solid ${selectedAccount.balance >= parseFloat(formData.amount) ? "#1890ff" : "#ff4d4f"}`,
                  fontSize: "13px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <span><strong>Account Balance:</strong> {formatAmount(selectedAccount.balance || 0)}</span>
                <span><strong>Balance After:</strong> {formatAmount((selectedAccount.balance || 0) - parseFloat(formData.amount))}</span>
                {selectedAccount.balance < parseFloat(formData.amount) && (
                  <span style={{ color: "#ff4d4f", fontWeight: 600 }}>⚠️ Insufficient balance!</span>
                )}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={handleCancel} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || (selectedAccount && selectedAccount.balance < parseFloat(formData.amount || 0))}
            >
              {submitting ? (
                <><Loader2 className="animate-spin mr-2" size={16} />Creating...</>
              ) : (
                <>Create Expense<Save className="ml-2" size={16} /></>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Expenses;
